import type { OfferInputData } from '../../../../../hooks/Marketing';
import { withDefaultTime } from '../dateUtils';

export const NO_DISCOUNT_ID = -1;

export const DISCOUNT_TYPE_OPTIONS = [
  { id: NO_DISCOUNT_ID, label: 'Pas de remise' },
  { id: 1, label: '% ou € sur le montant\ntotal de la transaction' },
  { id: 2, label: 'Mouvements de\npoints' },
  { id: 3, label: "Calcul d'un % sur le montant\ntotal de la dernière transaction" },
] as const;

export const TRIGGER_OPTIONS = [
  { id: 'preedite', label: 'Offre pré-éditée' },
  { id: 'montant', label: 'Par un montant de\ntransaction dépassé' },
  { id: 'article', label: "Par un nombre\nd'article" },
  { id: 'points', label: 'Par un seuil de\npoint dépassé' },
  { id: 'passage', label: 'Au prochain\npassage en caisse' },
  { id: 'action', label: "Par une action client\n(Offre à la demande)" },
  { id: 'inscription', label: "Par l'inscription au\nprogramme de fid (EAC)" },
] as const;

export const NO_PREREQ_ID = 'aucun';

export const PREREQ_OPTIONS = [
  { id: NO_PREREQ_ID, label: 'Pas de prérequis' },
  { id: 'montant', label: 'Par un montant de\ntransaction dépassé' },
  { id: 'article', label: "Par un nombre\nd'article" },
  { id: 'points', label: 'Par un seuil de\npoint dépassé' },
] as const;

type TriggerId = (typeof TRIGGER_OPTIONS)[number]['id'];

// Condition de l'édition sur deux niveaux : une condition racine, et pour « Au prochain passage en caisse » et
// « Offre à la demande » des sous-conditions cumulables. « Sans condition » = aucune sous-condition.
const triggerLabel = (id: TriggerId) => TRIGGER_OPTIONS.find(option => option.id === id)?.label ?? id;

const TRIGGER_ROOT_IDS = ['preedite', 'passage', 'action', 'inscription'] as const;
export type TriggerRootId = (typeof TRIGGER_ROOT_IDS)[number];
export const TRIGGER_ROOT_OPTIONS = TRIGGER_ROOT_IDS.map(id => ({ id, label: triggerLabel(id) }));

const TRIGGER_CONDITION_IDS = ['montant', 'article', 'points'] as const;
export type TriggerConditionId = (typeof TRIGGER_CONDITION_IDS)[number];

const isTriggerCondition = (value: unknown): value is TriggerConditionId =>
  TRIGGER_CONDITION_IDS.includes(value as TriggerConditionId);

// Sous-conditions proposées par condition racine (pas de nombre d'articles pour l'offre à la demande),
// et clé sous laquelle leur liste est enregistrée dans payloadRefEditCondition
const SUB_CONDITIONS: Partial<Record<TriggerRootId, { ids: readonly TriggerConditionId[]; payloadKey: string }>> = {
  passage: { ids: ['montant', 'article', 'points'], payloadKey: 'passageConditions' },
  action: { ids: ['montant', 'points'], payloadKey: 'actionConditions' },
};

export const triggerConditionOptions = (root: TriggerRootId | null) =>
  (root ? SUB_CONDITIONS[root]?.ids ?? [] : []).map(id => ({ id, label: triggerLabel(id) }));

export const hasTriggerConditions = (root: TriggerRootId | null) => root !== null && SUB_CONDITIONS[root] !== undefined;

export const triggerConditionsPayloadKey = (root: TriggerRootId | null) => (root ? SUB_CONDITIONS[root]?.payloadKey : undefined);

// Champs min/max à afficher pour la condition d'édition choisie
export const triggerFieldKinds = (o: Pick<OfferFormData, 'triggerId' | 'triggerConditions'>): string[] =>
  hasTriggerConditions(o.triggerId) ? o.triggerConditions : [];
type PrereqId = (typeof PREREQ_OPTIONS)[number]['id'];

const EDIT_CONDITION_ID_BY_TRIGGER: Partial<Record<TriggerId, number>> = {
  montant: 1, article: 2, points: 3, passage: 4, action: 5, inscription: 6,
  // Anciennement « Immédiat » : même identifiant, désormais « Offre pré-éditée »
  preedite: 7,
};

// Une seule sous-condition de passage garde son identifiant historique ; plusieurs (ou aucune) = « passage » (4),
// le détail étant dans payloadRefEditCondition.passageConditions. L'offre à la demande reste toujours 5.
export const editConditionId = (o: Pick<OfferFormData, 'triggerId' | 'triggerConditions'>): number | undefined => {
  if (o.triggerId === 'passage' && o.triggerConditions.length === 1) return EDIT_CONDITION_ID_BY_TRIGGER[o.triggerConditions[0]];
  return o.triggerId ? EDIT_CONDITION_ID_BY_TRIGGER[o.triggerId] : undefined;
};

const TRIGGER_BY_EDIT_CONDITION_ID: Record<number, TriggerId> = {
  1: 'montant', 2: 'article', 3: 'points', 4: 'passage', 5: 'action', 6: 'inscription', 7: 'preedite',
};

// « Pas de prérequis » n'envoie aucune condition d'utilisation
export const USE_CONDITION_ID_BY_PREREQ: Partial<Record<PrereqId, number>> = {
  montant: 1, article: 2, points: 3,
};

// 4 = ancien choix « Immédiat », remplacé par « Pas de prérequis »
const PREREQ_BY_USE_CONDITION_ID: Record<number, PrereqId> = {
  1: 'montant', 2: 'article', 3: 'points', 4: NO_PREREQ_ID,
};

export type ConditionPrefix = 'trigger' | 'prereq';
export type ConditionKind = 'Montant' | 'Article' | 'Points';
type ConditionKey = `${ConditionPrefix}${ConditionKind}${'Min' | 'Max'}`;

const CONDITION_KINDS: readonly ConditionKind[] = ['Montant', 'Article', 'Points'];

export const conditionKeys = (prefix: ConditionPrefix): ConditionKey[] =>
  CONDITION_KINDS.flatMap(kind => [`${prefix}${kind}Min`, `${prefix}${kind}Max`] as const);

const ALL_CONDITION_KEYS = [...conditionKeys('trigger'), ...conditionKeys('prereq')];

export interface OfferFormData extends Record<ConditionKey, string> {
  id: string;
  title: string;
  offerNumber: string;
  description: string;
  startValidityDate: string;
  endValidityDate: string;
  nbMaxEdit: string;
  nbMaxUse: string;
  discountTypeId: number | null;
  remiseMode: 'montant' | 'pourcentage';
  remiseValue: string;
  pointsMode: 'ajout' | 'multiplication';
  pointsValue: string;
  triggerId: TriggerRootId | null;
  triggerConditions: TriggerConditionId[];
  prereqId: PrereqId | null;
  // Option « Immediate use », indépendante de la condition d'utilisation : stockée dans payloadRefUseCondition
  immediateUse: boolean;
  // Offre cumulable avec d'autres offres (« Non » par défaut), stockée dans payloadRefUseCondition
  cumulable: boolean;
}

const readConditions = (read: (key: ConditionKey) => string | undefined) =>
  Object.fromEntries(ALL_CONDITION_KEYS.map(key => [key, read(key) || ''])) as Record<ConditionKey, string>;

export const emptyOffer = (id: string): OfferFormData => ({
  id,
  title: '',
  offerNumber: '',
  description: '',
  startValidityDate: '',
  endValidityDate: '',
  nbMaxEdit: '0',
  nbMaxUse: '1',
  discountTypeId: null,
  remiseMode: 'montant',
  remiseValue: '',
  pointsMode: 'ajout',
  pointsValue: '',
  triggerId: null,
  triggerConditions: [],
  prereqId: NO_PREREQ_ID,
  immediateUse: false,
  cumulable: false,
  ...readConditions(() => ''),
});

const triggerFromApi = (editConditionId: number | null, edit: Record<string, unknown>) => {
  const trigger = editConditionId ? TRIGGER_BY_EDIT_CONDITION_ID[editConditionId] ?? null : null;
  if (trigger !== null && isTriggerCondition(trigger)) return { triggerId: 'passage' as const, triggerConditions: [trigger] };
  const root = trigger as TriggerRootId | null;
  const key = triggerConditionsPayloadKey(root);
  const saved = key ? edit[key] : undefined;
  if (Array.isArray(saved)) return { triggerId: root, triggerConditions: saved.filter(isTriggerCondition) };
  // Anciennes offres à la demande : seules des bornes en points existaient
  const legacyPoints = root === 'action' && (edit.triggerPointsMin || edit.triggerPointsMax);
  return { triggerId: root, triggerConditions: legacyPoints ? ['points' as const] : [] };
};

export const offerInputToForm = (o: OfferInputData): OfferFormData => {
  const discount = o.payloadRefDiscountType ?? {};
  const edit = o.payloadRefEditCondition ?? {};
  const use = o.payloadRefUseCondition ?? {};
  return {
    id: String(o.id),
    title: o.title ?? '',
    description: o.description ?? '',
    offerNumber: o.offerNumber ?? '',
    startValidityDate: withDefaultTime(o.startValidityDate),
    endValidityDate: withDefaultTime(o.endValidityDate, true),
    nbMaxEdit: o.nbMaxEdit != null ? String(o.nbMaxEdit) : '',
    nbMaxUse: o.nbMaxUse != null ? String(o.nbMaxUse) : '',
    discountTypeId: o.refDiscountTypeId || null,
    remiseMode: discount.remiseMode === 'pourcentage' ? 'pourcentage' : 'montant',
    remiseValue: discount.remiseValue || '',
    pointsMode: discount.pointsMode === 'multiplication' ? 'multiplication' : 'ajout',
    pointsValue: discount.pointsValue || '',
    ...triggerFromApi(o.refEditConditionId, edit),
    prereqId: o.refUseConditionId ? PREREQ_BY_USE_CONDITION_ID[o.refUseConditionId] ?? null : NO_PREREQ_ID,
    immediateUse: use.immediateUse === true || use.immediateUse === 'true',
    cumulable: use.cumulable === true || use.cumulable === 'true',
    ...readConditions(key => {
      const value = key.startsWith('trigger') ? edit[key] : use[key];
      return typeof value === 'string' ? value : undefined;
    }),
  };
};

export const hasOfferContent = (o: OfferFormData) =>
  o.title.trim() !== '' || o.description.trim() !== '' || o.offerNumber.trim() !== '';
